// src/services/resultInsights.js
//
// Turns one exam attempt into the results page's insights: why each wrong answer
// was wrong, and which strengths, focus areas and traps to show. Pure and
// synchronous: no Firebase, no React, so the same answers always give the same page.
//
// It reads the smart tags inside the exam paper (question.smartTags, part.smartTags)
// and the shared taxonomy (public/data/result-tags/taxonomy.json). The rules it
// applies are the taxonomy's `rules`; doc/RESULTS_TAG_TAXONOMY.md explains them.

import { isAnswerCorrect, isVerdictQuestion } from '../utils/answerCheck';
import { TELC_PART_MAX } from '../utils/telcPartPoints';
import { cellKey, isCellCorrect, isTableGapQuestion } from '../utils/tableAnswers';
import { isNotesPart } from '../utils/notesAnswers';

const SECTION_OF = { reading: 'lesen', lesen: 'lesen', grammar: 'sprachbausteine', sprachbausteine: 'sprachbausteine', listening: 'hoeren', 'hören': 'hoeren', hoeren: 'hoeren' };
const MIN_ITEMS = 3;        // a family needs this many items on the paper to say anything
const MIN_WRONG = 2;        // …and this many misses to become a focus area
const STRONG_RATIO = 0.8;
const MAX_FOCUS = 3;

const familyOf = (id) => (id ? String(id).split('.')[0] : null);
const norm = (v) => String(v ?? '').trim().toLowerCase();
// Typed notes: ÖSD C1 lecture (marked by key) and telc C1 Hören 3 (marked by the AI rater, 0-2 points).
const isNotes = (part) => isNotesPart(part) || part?.taskType === 'information-transfer-notes';
const isBlank = (v) => v === undefined || v === null || v === '' || (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0);

/** Points one question is worth: telc B1/B2 weight parts as printed; otherwise the section shares its points evenly. */
function itemValue(exam, section, part, partSize, sectionSize) {
  const provider = norm(exam.examProvider);
  const level = norm(exam.level);
  // ÖSD B1 prints the same points per Aufgabe as telc B1/B2 (examService's OSD_B1_PART_POINTS).
  if (((provider === 'telc' && (level === 'b1' || level === 'b2')) || (provider === 'osd' && level === 'b1')) && TELC_PART_MAX[part.partId]) {
    return TELC_PART_MAX[part.partId] / partSize;
  }
  return (Number(section.totalPoints) || 0) / sectionSize;
}

/**
 * Why a wrong answer is wrong, from the tags. `kind` tells the page which sentence to show:
 * trap (a known trap, with `why` for this exact choice), reason (Sprachbausteine: form, meaning…),
 * other-key (the choice is the answer to another question), unrelated (a choice nobody tagged:
 * show its gist), slip (a literal true statement answered "falsch"), skipped.
 */
function causeOf({ q, part, answer, tags, partTags, keyOwner }) {
  if (isBlank(answer)) return { kind: 'skipped' };
  if (isVerdictQuestion(q, part)) {
    if (tags.literal) return { kind: 'slip' };
    return { kind: 'trap', trap: tags.trap || null, lure: tags.lure || null };
  }
  // ÖSD B2 Hören table row: the row's trap and the first institution noted wrongly with its answer.
  // (cellEvidence is raw Whisper text for checking; the explanation below the cause quotes cleanly.)
  if (isTableGapQuestion(q) && tags.cellEvidence) {
    const cell = q.cells.find((c) => !isCellCorrect(c, answer?.[cellKey(c)]));
    const name = part.table?.columns?.find((c) => c.id === cell?.col)?.shortName;
    const right = cell && [].concat(cell.correctAnswer).join(', ');
    return { kind: 'trap', trap: tags.trap || null, lure: tags.lure || null,
      why: name && right ? { de: `${name}: richtig ist „${right}“.`, en: `${name}: the answer is '${right}'.` } : null };
  }
  // Typed notes: the blank's trap and the key's first wording.
  if (isNotes(part) && tags.trap) {
    const right = [].concat(q.correctAnswer).join(' / ').split(/\s+\/\s+/)[0];
    return { kind: 'trap', trap: tags.trap, lure: tags.lure || null,
      why: { de: `Richtig ist „${right}“.`, en: `The answer is '${right}'.` } };
  }
  const chosen = norm(answer);
  const key = norm(q.correctAnswer);
  if (part.content?.wordBank) {
    const other = keyOwner.get(chosen);
    if (other && other !== q.questionId) return { kind: 'other-key', questionId: other };
    const classes = partTags.wordClasses || {};
    const classOf = (w) => classes[Object.keys(classes).find((k) => norm(k) === norm(w))];
    if (classOf(answer) && classOf(q.correctAnswer) && classOf(answer) !== classOf(q.correctAnswer)) {
      return { kind: 'reason', reason: 'structure.word-class' };
    }
    return { kind: 'point' };
  }
  // Typed gaps (ÖSD B2/C1): a listed wrong form of the right word, else the gap's language point.
  if (!q.options?.length && tags.point) {
    const typed = (v) => norm(v).replace(/[-.,;:!?]+$/, '');
    const form = Object.entries(tags.wrongForms || {}).find(([w]) => typed(w) === typed(answer));
    return form ? { kind: 'reason', reason: form[1] } : { kind: 'point' };
  }
  if (tags.reasons && tags.reasons[chosen]) return { kind: 'reason', reason: tags.reasons[chosen] };
  const decoy = tags.decoys && (tags.decoys[chosen] || tags.decoys[String(answer)]);
  if (decoy) return { kind: 'trap', trap: decoy.trap, why: decoy.why, failed: decoy.failed || null };
  // "no text fits": x on the telc answer sheet, 0 on the ÖSD one
  const none = (v) => v === 'x' || v === '0';
  if (none(chosen) && !none(key)) return { kind: 'trap', trap: 'no-match.x-overcautious', why: tags.ifChoseX?.why || null };
  if (none(key)) return { kind: 'trap', trap: 'no-match.forced-match', why: tags.xReason || null, gist: partTags.optionGists?.[chosen] || null };
  const other = keyOwner.get(chosen);
  if (other && other !== q.questionId) return { kind: 'other-key', questionId: other, gist: partTags.optionGists?.[chosen] || null };
  return { kind: 'unrelated', gist: partTags.optionGists?.[chosen] || null };
}

/** Every trap family a question can catch a student with (exposure for the focus rules). */
function trapFamiliesOn(q, part, tags) {
  const out = new Set();
  // The lure is only why a trap works (e.g. the same word was heard); the lesson is the trap itself.
  if ((isVerdictQuestion(q, part) || isTableGapQuestion(q) || isNotes(part)) && tags.trap) out.add(familyOf(tags.trap));
  Object.values(tags.decoys || {}).forEach((d) => d?.trap && out.add(familyOf(d.trap)));
  if (tags.ifChoseX || tags.xReason) out.add('no-match');
  return out;
}

/**
 * @param {object} args
 * @param {object} args.exam          exam paper JSON (with smartTags)
 * @param {object} args.userAnswers   { [partId]: { [questionId]: answer } }
 * @param {string[]} [args.flagged]   question ids the student flagged (digital simulator only)
 * @param {boolean} [args.hasFlags]   false for paper mode: no flags exist, so nobody counts as "sure"
 * @param {object} args.taxonomy      public/data/result-tags/taxonomy.json
 * @param {object[]} [args.writingCorrections]  writingFeedback.corrections; those with a `tag`
 *   (a language point id, set by the correction function) count as evidence for that family
 */
export function buildResultInsights({ exam, userAnswers = {}, flagged = [], hasFlags = true, taxonomy, writingCorrections = [] }) {
  const flaggedSet = new Set(flagged || []);
  const languageFamilyOf = Object.fromEntries((taxonomy?.languagePoints || []).map((p) => [p.id, p.family]));
  const items = [];
  const sections = {};

  (exam.sections || []).forEach((section) => {
    const sectionKey = SECTION_OF[norm(section.sectionId)];
    if (!sectionKey) return;   // Schreiben/Sprechen are not marked item by item
    const parts = (section.parts || []).map((part) => ({ part, qs: (part.questions || []).filter((q) => !q.isExample) })).filter((p) => p.qs.length);
    const sectionSize = parts.reduce((n, p) => n + p.qs.length, 0);
    if (!sectionSize) return;
    const sec = sections[sectionKey] || (sections[sectionKey] = { id: sectionKey, points: 0, max: 0, correct: 0, total: 0 });

    parts.forEach(({ part, qs }) => {
      const partTags = part.smartTags || {};
      const keyOwner = new Map(qs.map((q) => [norm(q.correctAnswer), q.questionId]));
      qs.forEach((q) => {
        const answer = userAnswers?.[part.partId]?.[q.questionId];
        const answered = !isBlank(answer);
        // telc C1 notes carry the rater's points (userAnswers._notesPoints); 2 = full marks
        const rated = part.taskType === 'information-transfer-notes' && userAnswers?._notesPoints
          ? (userAnswers._notesPoints[q.questionId] ?? 0) : null;
        const correct = answered && (rated !== null ? rated >= 2 : isAnswerCorrect(answer, q, part));
        const value = itemValue(exam, section, part, qs.length, sectionSize);
        const tags = q.smartTags || {};
        const isFlagged = flaggedSet.has(q.questionId);
        const item = {
          section: sectionKey, partId: part.partId, questionId: q.questionId,
          number: q.questionNumber ?? q.blankNumber ?? null,
          answer: answered ? answer : null, correctAnswer: q.correctAnswer,
          correct, answered, flagged: isFlagged,
          // Hypercorrection: a confident mistake is the one most worth reviewing.
          confident: hasFlags && answered && !correct && !isFlagged,
          value, lost: correct ? 0 : rated ? value * (2 - rated) / 2 : value,
          skill: tags.skill || null, point: tags.point || null,
          languageFamily: tags.point ? (languageFamilyOf[tags.point] || null) : null,
          trapFamilies: [...trapFamiliesOn(q, part, tags)],
          evidence: tags.evidence || null,
          cause: correct ? null : causeOf({ q, part, answer, tags, partTags, keyOwner }),
        };
        items.push(item);
        sec.max += value; sec.total += 1;
        if (correct) { sec.points += value; sec.correct += 1; }
      });
    });
  });

  // ---- families: skills, traps and language points, each counted over the items that carry them
  const families = new Map();
  const bump = (kind, id, item, missed) => {
    if (!id) return;
    const key = `${kind}:${id}`;
    const f = families.get(key) || { key, kind, id, items: 0, wrong: 0, lost: 0, sections: new Set(), wrongItems: [] };
    f.items += 1;
    if (missed) { f.wrong += 1; f.lost += item.value; f.sections.add(item.section); f.wrongItems.push(item.questionId); }
    families.set(key, f);
  };
  items.forEach((it) => {
    const wrong = !it.correct;
    bump('skill', familyOf(it.skill), it, wrong);
    bump('language', it.languageFamily, it, wrong);
    // A trap only counts as "caught" when the student's choice actually fell into that trap.
    const caught = new Set();
    if (it.cause?.kind === 'trap') caught.add(familyOf(it.cause.trap));
    it.trapFamilies.forEach((fam) => bump('trap', fam, it, caught.has(fam)));
  });
  // Errors in the student's own text: evidence for the same language families, no points
  // of their own (Schreiben is marked as a whole, not error by error).
  (writingCorrections || []).forEach((c, i) => {
    const fam = c?.tag ? languageFamilyOf[c.tag] : null;
    if (fam) bump('language', fam, { section: 'schreiben', value: 0, questionId: `w${i}` }, true);
  });
  const all = [...families.values()].map((f) => ({ ...f, sections: [...f.sections], ratio: f.items ? (f.items - f.wrong) / f.items : 0 }));

  // ---- focus areas: enough evidence, ranked by points to gain. Traps and language points
  // say what to do, so they go before skills; a skill that mostly repeats a chosen area is dropped.
  // Traps and language points pick first (they name what to do); skills only fill free slots.
  const actionable = (f) => (f.kind === 'skill' ? 1 : 0);
  const candidates = all
    .filter((f) => f.items >= MIN_ITEMS && f.wrong >= MIN_WRONG)
    .sort((a, b) => (actionable(a) - actionable(b)) || (b.lost - a.lost));
  const focus = [];
  candidates.forEach((f) => {
    if (focus.length >= MAX_FOCUS) return;
    const overlaps = focus.some((g) => f.wrongItems.filter((q) => g.wrongItems.includes(q)).length > f.wrongItems.length / 2);
    if (!overlaps) focus.push(f);
  });
  focus.forEach((f) => { f.crossSection = f.sections.length > 1; });
  focus.sort((a, b) => b.lost - a.lost);   // shown by points to gain

  // ---- strengths: enough items and (nearly) all right; small families must be perfect.
  const strong = all
    .filter((f) => f.kind !== 'trap' && f.items >= MIN_ITEMS && (f.items <= 4 ? f.wrong === 0 : f.ratio >= STRONG_RATIO))
    .filter((f) => !focus.some((g) => g.key === f.key))
    .sort((a, b) => (b.ratio - a.ratio) || (b.items - a.items));
  const avoidedTraps = all
    .filter((f) => f.kind === 'trap' && f.items >= MIN_ITEMS && f.wrong === 0)
    .sort((a, b) => b.items - a.items);

  // ---- sections and the attempt as a whole
  const round1 = (v) => Math.round(v * 10) / 10;
  const sectionList = Object.values(sections).map((s) => ({ ...s, points: round1(s.points), max: round1(s.max), ratio: s.max ? s.points / s.max : 0 }));
  const best = [...sectionList].sort((a, b) => b.ratio - a.ratio)[0] || null;
  const wrongItems = items.filter((it) => !it.correct);
  const review = [...wrongItems].sort((a, b) =>
    (Number(b.confident) - Number(a.confident)) || (b.lost - a.lost) || ((a.number ?? 0) - (b.number ?? 0)));

  return {
    items,
    sections: sectionList,
    focus,
    // Never an empty first screen: without a qualifying strength, name the best section.
    strengths: strong.length ? strong.slice(0, 2) : (best ? [{ key: `section:${best.id}`, kind: 'section', id: best.id, ratio: best.ratio }] : []),
    avoidedTraps: avoidedTraps.slice(0, 3),
    review,
    counts: {
      wrong: wrongItems.length,
      confident: wrongItems.filter((it) => it.confident).length,
      skipped: wrongItems.filter((it) => it.cause?.kind === 'skipped').length,
      slips: wrongItems.filter((it) => it.cause?.kind === 'slip').length,
    },
    families: all,
  };
}

export default buildResultInsights;
